<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import {
  AvatarRoot,
  AvatarImage,
  AvatarFallback,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  TooltipPortal,
  TooltipContent,
} from 'reka-ui';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import LoopInPopover from './LoopInPopover.vue';
import MoreActions from './MoreActions.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { removeEmoji } from 'shared/helpers/emoji';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const STATUS_DOT_CLASSES = {
  online: 'bg-n-teal-10',
  busy: 'bg-n-amber-10',
};

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const contactInitials = computed(() => {
  const name = currentContact.value?.name;
  if (!name) return '';
  return removeEmoji(name)
    .split(/\s+/)
    .slice(0, 2)
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase();
});

const statusDotClass = computed(
  () => STATUS_DOT_CLASSES[currentContact.value?.availability_status]
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !currentContact.value?.blocked
);

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="relative flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12 bg-n-surface-1 after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-gradient-to-r after:from-n-lubb-cyan/70 after:via-n-lubb-violet/70 after:to-n-lubb-violet/0"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="me-2"
      />
      <span
        class="relative flex-shrink-0 rounded-full p-[2px] bg-gradient-to-br from-n-lubb-cyan to-n-lubb-violet"
      >
        <AvatarRoot
          class="inline-flex size-8 items-center justify-center overflow-hidden rounded-full bg-n-surface-1 align-middle"
        >
          <AvatarImage
            v-if="currentContact.thumbnail"
            :src="currentContact.thumbnail"
            :alt="currentContact.name"
            class="size-full object-cover"
          />
          <AvatarFallback
            class="flex size-full items-center justify-center bg-n-brand/10 text-xs font-semibold leading-none text-n-brand"
          >
            {{ contactInitials }}
          </AvatarFallback>
        </AvatarRoot>
        <span
          v-if="statusDotClass"
          class="absolute bottom-0 end-0 size-2.5 rounded-full ring-2 ring-n-surface-1"
          :class="statusDotClass"
        />
      </span>
      <div class="flex flex-col items-start min-w-0 ms-2.5 overflow-hidden">
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span
            class="text-sm font-semibold truncate leading-tight text-n-slate-12"
          >
            {{ currentContact.name }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <TooltipProvider :delay-duration="300">
          <div
            class="flex items-center gap-1.5 overflow-hidden conversation--header--actions text-ellipsis whitespace-nowrap mt-0.5"
          >
            <TooltipRoot>
              <TooltipTrigger as-child>
                <button
                  type="button"
                  class="inline-flex items-center h-4 rounded px-1.5 text-[11px] font-medium leading-none tabular-nums bg-n-brand/10 text-n-brand transition-colors hover:bg-n-brand/20 cursor-pointer"
                  @click="copyConversationId"
                >
                  {{ `#${chat.id}` }}
                </button>
              </TooltipTrigger>
              <TooltipPortal>
                <TooltipContent
                  side="bottom"
                  :side-offset="6"
                  class="z-50 rounded-md bg-n-slate-12 px-2 py-1 text-xs text-n-slate-1 shadow-md"
                >
                  {{ $t('CONVERSATION.HEADER.COPY_ID') }}
                </TooltipContent>
              </TooltipPortal>
            </TooltipRoot>
            <InboxName
              v-if="hasMultipleInboxes"
              :inbox="inbox"
              class="inline-flex h-6 gap-1 rounded-md bg-n-slate-3 px-2 !mx-0 [&_span]:text-sm [&_span]:font-semibold [&_span]:leading-none [&_span]:text-n-slate-12 [&_svg]:size-4 [&_svg]:text-n-slate-12"
            />
            <span
              v-if="isSnoozed"
              class="inline-flex items-center h-4 rounded px-1.5 text-[11px] font-medium leading-none bg-n-amber-3 text-n-amber-11"
            >
              {{ snoozedDisplayText }}
            </span>
          </div>
        </TooltipProvider>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <ConversationCallButton :inbox="inbox" :chat="currentChat" />
      <LoopInPopover />
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
